import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import ContainerGrid from './ContainerGrid';
import AuthContext from './auth/Context';
import { loadBabelOnce, EmitEvents } from '../utils';
import { useQuickAction } from './DataQuickActionContext';//Mở/tạo nhanh chứng từ (openEdit/openView/openAdd) từ trong trang tuỳ biến
import MarkdownView, { slugify } from './MarkdownView';//Trang tài liệu: <Markdown>{noiDung}</Markdown>
import TichHopNhung from './TichHopNhung';//Nhúng màn hình cấu hình kết nối: <TichHop loai="ads" />
import fetchXacThucGoc from './auth/fetchXacThuc';//fetch có xử lý phiên đăng nhập (token hết hạn -> tự làm mới / mở form đăng nhập)
import { bienDichComponent } from './bienDichJsx';
import { bocScriptTrang } from './customPageSandbox';//Cách bọc script + danh sách đồ nghề (dùng chung với test backend)
import { BO_O_NHAP } from './CustomPageFields';//Ô nhập như form: NumberField, DateField, DateTimeField, TimeField, MonthField, RichTextEditor, CodeEditor, JsonEditor, FieldControl
import LookupFieldControl from './LookupFieldControl';//Ô tìm-chọn (autocomplete) Y HỆT form DataListManager: <LookupField field={{ type: 'Autocomplete', api, apiMapping }} … />//Biên dịch script JSX -> component (an toàn với hàm phụ Babel chèn đầu mã)

// Hộp "Hướng dẫn" dùng chung (HuongDanDialog — giống nút Hướng dẫn của usermodule) cấp cho script dưới tên `HuongDanDialog`.
// Nạp lười: chỉ tải react-markdown khi hộp mở lần đầu. Định nghĩa ở cấp module để giữ nguyên danh tính component giữa các lần render.
const HuongDanDialogLazy = React.lazy(() => import('./HuongDanDialog'));
const HuongDanTrang = (p) => (p && p.open
  ? <React.Suspense fallback={null}><HuongDanDialogLazy {...p} /></React.Suspense>
  : null);
// Bản này nhận `noiDung` (Markdown dựng sẵn — vd hướng dẫn kèm code hiện tại của trang) ngoài `url`; script kiểm cờ trước khi dùng.
HuongDanTrang.hoTroNoiDung = true;

// Thư viện sơ đồ kéo-thả (reactflow) cho trang tuỳ biến: CHỈ nạp khi script nhắc tới `ReactFlow` (đa số trang không cần,
// không bắt mọi trang tải thêm ~150KB). Nạp một lần, kèm CSS. Lỗi nạp -> trang vẫn chạy, script nhận ReactFlow = null.
let _napReactFlow = null;
const napReactFlow = () => {
  if (!_napReactFlow) {
    _napReactFlow = Promise.all([import('reactflow'), import('reactflow/dist/style.css')])
      .then(([m]) => m)
      .catch((e) => { console.warn('[DynamicCustomPage] không nạp được reactflow:', e); _napReactFlow = null; return null; });
  }
  return _napReactFlow;
};

// =====================================================================
// TRANG TUỲ BIẾN (custom page) của usermodule.
//
// Dùng ở HAI nơi:
//   1. Trang đầy đủ  -> /#/page/custom/<code>
//   2. Widget trong Dynamic Dashboard -> <DynamicCustomPage code=... embedded />
// `embedded` CHỈ đổi các màn hình trạng thái (đang tải / lỗi / không tìm thấy) cho vừa ô widget —
// bản đầy đủ chiếm 100dvh và có màn 404 cỡ lớn, nhét nguyên vào một ô 300px thì vỡ bố cục.
// Phần biên dịch và "đồ nghề" cấp cho script thì GIỐNG HỆT nhau, để một trang chạy được ở trang
// đầy đủ là chắc chắn chạy được trong widget.
// Lấy cấu hình 'custompage' theo code -> biên dịch runtime (Babel) chuỗi JSX (script)
// thành React component -> render, cấp sẵn: React (kèm hooks), MUI, ContainerGrid,
// context (APIs/auth), socket (socket.io) để làm realtime, và props tĩnh của trang.
//   /#/page/custom/<code>
// =====================================================================
const DynamicCustomPage = ({ code, history, location, embedded = false }) => {
  const context = useContext(AuthContext);
  const quickAction = useQuickAction();//DataQuickActionContext: openAdd/openEdit/openView
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  // 1) Lấy cấu hình trang theo code (model 'custompage', dùng chung — không theo id_app)
  const { data: page, isLoading, isError, error } = useQuery({
    queryKey: ['custompage_config', code],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'custompage', { condition: { code }, limit: 1 });
      const item = res?.data?.[0] || res?.[0];
      return item ? (item.toObject ? item.toObject() : (item._doc || item)) : null;
    },
    enabled: !!code && !!userToken,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // 2) Biên dịch script (JSX) khi có cấu hình
  const [compiled, setCompiled] = useState({ Component: null, MUI: null, ReactFlow: null, error: null, loading: false });

  useEffect(() => {
    let alive = true;
    const compile = async () => {
      if (!page || !page.script) { setCompiled({ Component: null, MUI: null, ReactFlow: null, error: null, loading: false }); return; }
      setCompiled((p) => ({ ...p, loading: true, error: null }));
      try {
        const canSoDo = /\bReactFlow\b/.test(page.script);
        const [Babel, MUI, ReactFlow] = await Promise.all([loadBabelOnce(), import('@mui/material'), canSoDo ? napReactFlow() : null]);
        // Component nhận đủ "đồ nghề" — script dùng React.useState/useEffect, MUI.*, socket, context...
        // `Markdown` + `slugify`: trang tài liệu chỉ cần <Markdown>{chuoi}</Markdown>, không phải tự
        // viết trình render markdown trong sandbox.
        // `fetchXacThuc(url, init)` + `fetch` CHE BÓNG fetch toàn cục: script trang tuỳ biến (kể cả script khách tự
        // viết, nằm trong DB — không sửa được từng cái) gọi `fetch` tới máy chủ mà token hết hạn thì tự làm mới +
        // chạy lại, phiên chết thì mở form đăng nhập — thay vì hỏng tới khi người dùng đăng xuất/đăng nhập lại.
        // Request không mang token (dịch vụ bên ngoài, /docs) đi y như fetch thường. Xem auth/fetchXacThuc.js.
        // `LookupField`: ô tìm-chọn của form DataListManager (LookupFieldControl) — gõ là tìm gần đúng theo tên / mã (+ field.searchFields),
        // mở là gợi ý sẵn, giữ nhãn đã chọn. Props: field { type:'Autocomplete'|'Select', api, apiMapping:{value,label,label_as}, apiFields,
        // apiCondition, searchFields, label }, value, formData (để hiện nhãn), onChange(giaTri, { value, label, raw }).
        // `ReactFlow`: CẢ module reactflow (ReactFlow.default = khung sơ đồ, Handle, Position, MarkerType, Background,
        // Controls, MiniMap…) — chỉ có khi script nhắc tới ReactFlow; null nếu nạp lỗi (script phải tự lùi về giao diện thường).
        // Bộ ô nhập (01/11/2026): `NumberField`, `DateField`, `DateTimeField`, `TimeField`, `MonthField`, `RichTextEditor`, `CodeEditor`,
        // `JsonEditor`, `FieldControl` — cùng component với form DataListManager (BaseFieldControl). Khai ở hàm ngoài: script tự khai
        // trùng tên thì tên của script thắng, không lỗi cú pháp (xem customPageSandbox.js).
        const wrapped = bocScriptTrang(page.script);
        const Component = bienDichComponent(Babel, wrapped, ['React', 'MUI', 'ContainerGrid'], [React, MUI, ContainerGrid]);
        if (alive) setCompiled({ Component, MUI, ReactFlow: ReactFlow || null, error: null, loading: false });
      } catch (e) {
        console.error('[DynamicCustomPage] lỗi biên dịch script:\n', page && page.script, e);
        if (alive) setCompiled({ Component: null, MUI: null, ReactFlow: null, error: e.message, loading: false });
      }
    };
    compile();
    return () => { alive = false; };
  }, [page]);

  // ---- Trạng thái ----
  const Center = ({ children }) => (
    <Box sx={{
      height: embedded ? '100%' : '100dvh',
      display: 'flex', flexDirection: 'column', gap: 1.5,
      justifyContent: 'center', alignItems: 'center',
      bgcolor: embedded ? 'transparent' : '#f2f2f7', p: 2, textAlign: 'center'
    }}>{children}</Box>
  );

  if (isLoading) {
    return <Center><CircularProgress size={embedded ? 26 : 40} thickness={4} sx={{ color: 'text.secondary' }} /></Center>;
  }
  if (isError) {
    return <Alert severity="error" sx={{ m: embedded ? 1 : 2, borderRadius: 2 }}>Không tải được cấu hình trang: {error?.message}</Alert>;
  }
  if (!page) {
    // Trong widget: trang bị xoá/đổi mã sau khi ai đó đã gắn lên dashboard -> nói rõ để người dùng
    // gỡ widget, thay vì để một ô trống không giải thích gì.
    if (embedded) {
      return <Alert severity="warning" sx={{ m: 1, borderRadius: 2 }}>Trang tuỳ biến "{code}" không còn tồn tại. Gỡ widget này hoặc chọn trang khác.</Alert>;
    }
    return (
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f2f2f7', p: 3 }}>
        <Typography variant="h1" fontWeight="bold" color="text.secondary" sx={{ opacity: 0.5 }}>404</Typography>
        <Typography variant="h6" sx={{ mt: 2, mb: 3 }}>Trang tuỳ biến "{code}" chưa được cấu hình.</Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => history && history.push('/')} sx={{ borderRadius: 3, textTransform: 'none' }}>Quay về trang chủ</Button>
      </Box>
    );
  }
  if (compiled.loading) {
    return (
      <Center>
        <CircularProgress size={embedded ? 22 : 24} />
        {!embedded && <Typography color="text.secondary">Đang tải tài nguyên và biên dịch giao diện…</Typography>}
      </Center>
    );
  }
  if (compiled.error) {
    return <Alert severity="error" sx={{ m: embedded ? 1 : 2, borderRadius: 2 }}><strong>Lỗi biên dịch trang "{page.title || code}":</strong> {compiled.error}</Alert>;
  }
  const { Component, MUI, ReactFlow } = compiled;
  if (!Component || !MUI) return null;

  // socket dùng chung của app (đã đăng nhập) — cho phép trang làm realtime (inbox…)
  const socket = (EmitEvents && EmitEvents.socket && EmitEvents.socket.socket) || null;
  // fetch gắn sẵn context.apis (đọc LÚC GỌI để luôn là apis hiện tại) — cấp cho script cả tên `fetchXacThuc` lẫn `fetch`.
  const fetchXacThuc = (url, init) => fetchXacThucGoc(context.apis, url, init);

  return (
    <Component
      React={React}
      MUI={MUI}
      ContainerGrid={ContainerGrid}
      context={context}
      quickAction={quickAction}
      apis={context.apis}
      config={context.config}
      userInfo={context.userInfo}
      socket={socket}
      page={page}
      Markdown={MarkdownView}
      slugify={slugify}
      TichHop={TichHopNhung}
      fetchXacThuc={fetchXacThuc}
      fetch={fetchXacThuc}
      ReactFlow={ReactFlow}
      LookupField={LookupFieldControl}
      HuongDanDialog={HuongDanTrang}
      {...BO_O_NHAP}
      props={page.props || {}}
      history={history}
      location={location}
    />
  );
};

export default DynamicCustomPage;
